/**
 * ProductVariant component will display product variant such as color, type, scent and size to the user.
 *
 * @module views/components/ProductVariant
 * @memberof -Common
 */
import './ProductVariant.scss';

import React, { useCallback, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import AsyncComponent from '@ulta/core/components/AsyncComponent/AsyncComponent';
import Button from '@ulta/core/components/Button/Button';
import { useIntersectionObserver } from '@ulta/core/hooks/useIntersectionObserver/useIntersectionObserver';
import { useAppConfigContext } from '@ulta/core/providers/AppConfigProvider/AppConfigProvider';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';
// Required for datacapture impressions
import { constants } from '@ulta/core/utils/constants/constants';
import CMS_QUERY from '@ulta/core/utils/graphql/queries/cms/cms';
import { handleReplace } from '@ulta/core/utils/handleLocation/handleLocation';
import { handleIntersection } from '@ulta/core/utils/intersectionProcessor/intersectionProcessor';

import PillSelector from '@ulta/components/PillSelector/PillSelector';
import ProductDimension from '@ulta/components/ProductDimension/ProductDimension';
import ProductSwatches from '@ulta/components/ProductSwatches/ProductSwatches';
import SwatchDropDown from '@ulta/components/SwatchDropDown/SwatchDropDown';

import * as utils from './ProductVariant';

/**
 * Represents a ProductVariant component
 *
 * @method
 * @param {ProductVariantProps} props - React properties passed from composition
 * @returns ProductVariant
 */
export const ProductVariant = function( props ){
  const { inflection } = useDeviceInflection();
  const { previewDate } = useAppConfigContext();
  const { findationApiKey, findationTestingMode } = useAppConfigContext();

  const {
    variants,
    typeLabel,
    dimensionsLabel,
    dimensionsValue,
    viewAllAction,
    viewLessLabel,
    shadeFinderAction,
    doneAction,
    closeAccessibilityAction,
    collapsible,
    expanded,
    id,
    rootMargin = defaultProps.rootMargin,
    root = defaultProps.root,
    threshold = defaultProps.threshold,
    invokeAction,
    loading
  } = props;

  // Start Datacapture Impression - TODO: Genericise and Abstract into LayerHost
  const componentRef = useRef();
  useIntersectionObserver(
    componentRef,
    {
      root: root,
      rootMargin: rootMargin,
      threshold: threshold
    },
    handleIntersection( props )
  );
  // End Datacapture Impression
  const FINDATION_URL_PATTERN = 'findation.html';
  const [skuId, setSkuId] = useState();
  const [embedId, setEmbedId] = useState();
  // Update URL after variant selection
  const swatchResponseHandler = useCallback( composeSwatchResponse( { loading, skuId, previewDate } ), [loading, skuId, previewDate] );
  useEffect( swatchResponseHandler, [swatchResponseHandler] );

  // TODO - make a prepped query, we also need skuId in response to update URL after
  const updateSwatchPage = useCallback( utils.composeUpdateHandler( { setSkuId, invokeAction } ), [] );

  const hasVariants = variants?.length > 0;

  if( !hasVariants ){
    return null;
  }

  const isSingle = variants.length === 1;
  const isMultiple = variants.length > 1;

  const isColor = typeLabel?.toLowerCase() === 'color';
  const isColorMobile = isColor && inflection.MOBILE;
  const isColorDesktop = isColor && inflection.DESKTOP;
  const isPillSelector = !isColor;

  const isSize = typeLabel?.toLowerCase() !== 'size';
  const isShadeFinder =
    variants.length > 1 && inflection.DESKTOP && isColor && shadeFinderAction?.label && shadeFinderAction?.url;
  const shouldFetchEmbedId = variants.length > 1 && isColor && shadeFinderAction?.label && shadeFinderAction?.url;

  const hasDimensionInfo = isSingle || isSize || isShadeFinder;
  useEffect( () => {
    if( !shouldFetchEmbedId ){
      return;
    }
    utils.fetchEmbedId( { findationApiKey }, { setEmbedId } );
  }, [] );
  const handleClick = useCallback( async() => {
    utils.handleFindationClick( { url :shadeFinderAction.url, findationUrlPattern: FINDATION_URL_PATTERN, embedId, findationApiKey, findationTestingMode, variants }, { updateSwatchPage } );
  }, [embedId] );

  return (
    <div className='ProductVariant'
      id={ props.id }
      ref={ componentRef }
    >
      {
        /* Multiple Variants */
        isMultiple && (
          <>
            { isColorMobile && (
              <AsyncComponent
                closeAccessibilityAction={ closeAccessibilityAction }
                doneAction={ doneAction }
                invokeAction={ invokeAction }
                moduleName='ProductSwatchesMobile'
                onSwatchSelected={ updateSwatchPage }
                refetchContentId={ id }
                shadeFinderAction={ shadeFinderAction }
                typeLabel={ typeLabel }
                variants={ variants }
                viewAllAction={ viewAllAction }
                viewAllLabel={ viewAllAction?.label }
                handleFindationClick={ handleClick }
              />
            ) }
            { isColorDesktop && (
              <>
                <SwatchDropDown variants={ variants }
                  typeLabel={ typeLabel }
                  selectAction={ updateSwatchPage }
                />
                <ProductSwatches
                  viewLessLabel={ viewLessLabel }
                  viewAllLabel={ viewAllAction?.label }
                  viewAllAction={ viewAllAction }
                  variants={ variants }
                  collapsible={ collapsible }
                  expanded={ expanded }
                  selectAction={ updateSwatchPage }
                />
              </>
            ) }
            { isPillSelector && (
              <PillSelector
                variants={ variants }
                typeLabel={ typeLabel }
                selectAction={ updateSwatchPage }
              />
            ) }
          </>
        )
      }
      { hasDimensionInfo &&
      <div
        className={ classNames( 'ProductVariant__info', {
          'ProductVariant__info--dimension': isMultiple
        } ) }
      >
        { isSingle && (
          <ProductDimension
            type={ typeLabel }
            description={ singleVariantDescription( variants[0], typeLabel ) }
            swatch={ variants[0].swatchImage }
          />
        ) }

        { isSize && (
          <ProductDimension type={ dimensionsLabel }
            description={ dimensionsValue }
          />
        ) }

        { isShadeFinder && (
          <Button
            className='ProductVariant__shadeFinder'
            likeLink
            label={ shadeFinderAction.label }
            onClick={ handleClick }
          />
        ) }
      </div>
      }
    </div>
  );
};

/**
 * Replace URL with new skuId
 * @param {{ loading: boolean, skuId: string, previewDate: string }} data args
 */
export const composeSwatchResponse = ( data ) => () => {
  const { loading, skuId, previewDate } = data || {};
  if( loading !== false || !skuId ){
    return;
  }
  const url = new URL( global.location.href );
  url.searchParams.set( 'sku', skuId );
  url.pathname = global.location.pathname;
  handleReplace( { url: url.href, previewDate } );
};


/**
 * @callback onSwatchClick
 * @param {{ skuId: string }} item swatch item
 */

/**
 * Updates swatch page
 * @param {{skuId: {current: string} }} data args
 * @param {{ invokeAction: function }} methods methods
 * @returns {onSwatchClick} onSwatchClick handles a swatch click
 */
export const composeUpdateHandler = ( methods ) => ( item ) => {
  const { invokeAction, setSkuId } = methods || {};

  if( !invokeAction || !setSkuId || !item.skuId ){
    return;
  }

  setSkuId( item.skuId );

  const { origin, pathname } = global.location;
  const path = `${origin}${pathname}?sku=${item.skuId}`;
  invokeAction( {
    graphql: CMS_QUERY,
    method: item.selectAction.method,
    // TODO customheaders wil need to come from dxl definition in future
    customHeaders: [
      { key: 'X-ULTA-GRAPH-TYPE', value: 'query' },
      { key: 'X-ULTA-GRAPH-MODULE-NAME', value: 'ProductVariant' },
      { key: 'X-ULTA-GRAPH-SUB-TYPE', value: 'page' },
      { key: 'X-ULTA-GRAPH-PAGE-URL', value: path }
    ],
    variables: {
      url: {
        path
      }
    }
  } );
};

/**
 * Fetches the embed ID from the Findation API and sets it using the provided method.
 *
 * @param {Object} methods - An object containing methods for handling the fetched data.
 * @param {Function} methods.setEmbedId - A function to set the embed ID.
 */
export const fetchEmbedId = async( data, methods ) => {
  const { findationApiKey } = data;
  const { setEmbedId } = methods;
  const json_data = {
    api_key: findationApiKey,
    product_url: window.location.href
  };
  const response = await fetch( 'https://findation.com/api/embed', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify( json_data )
  } );

  if( response.ok ){
    const data = await response.json();
    setEmbedId( data.embed_id );
  }
};

export const singleVariantDescription = ( variant = {}, type = '' ) => {
  let description = variant && variant.name;
  if( type && type.toLowerCase() === 'color' && variant.shadeDescription ){
    description += ` (${variant.shadeDescription})`;
  }
  return description;
};

/**
 * Handles the click event for Findation link click.
 *
 * This function checks if the provided URL matches the Findation URL pattern.
 * If it does, it dynamically imports the Findation module and triggers the
 * Findation modal with the provided data. If the URL does not match, it redirects
 * the browser to the given URL.
 *
 * @param {Object} data - The data object containing the required data.
 * @param {string} data.url - The URL to be checked.
 * @param {string} data.findationUrlPattern - The pattern used to identify Findation URLs.
 * @param {string} data.embedId - The ID for Findation modal.
 * @param {string} data.findationApiKey - The API key for accessing Findation api.
 * @param {boolean} data.findationTestingMode - Flag to indicate if testing mode is enabled for Findation.
 * @param {Array} data.variants - An array of variant to be passed to the Findation modal.
 *
 * @param {Object} methods - The methods objects having helper functions.
 * @param {Function} methods.updateSwatchPage - A method to update the swatch page after clicking the Findation link.
 *
 */
export const handleFindationClick = async( data, methods ) => {
  const { url, findationUrlPattern, embedId, findationApiKey, findationTestingMode, variants } = data;
  const { updateSwatchPage } = methods;

  if( !url ){
    return;
  }

  if( url.includes( findationUrlPattern ) ){
    const { default: createAndShowFindationModal } = await import( '@ulta/innovation/Findation/Findation' );
    createAndShowFindationModal && createAndShowFindationModal( { embedId, api_key: findationApiKey, testing_mode: findationTestingMode, variants }, { updateSwatchPage } );
  }
  else {
    global.location.href = url ;
  }
};

/**
 * Property type definitions
 *
 * @typedef ProductVariantProps
 * @type {object}
 * @property {object.<{productId: string, skuId: string, name: string, shadeDescription: string, swatchImage: object, smooshImage: object, selected: boolean, disabled: boolean, unavailable: boolean, listPrice: string, salePrice: string, selectAction: object}>} variants - Sets the information of product's variants
 * @property {string} typeLabel - Sets the label of variant
 * @property {string} dimensionsLabel - Sets the dimensions label such as size of variant
 * @property {string} dimensionsValue - Sets the dimensions value for size of the variant
 * @property {object} shadeFinderAction - This is the action for shade finder
 * @property {object} doneAction - This is the action for the 'DONE' button on SwatchDetails
 * @property {object} closeAccessibilityAction - This is the action for the close button on SwatchDetails, mainly ADA labeling
 * @property {string} viewAllLabel - This is aria-label for expand button
 * @property {string} viewLessLabel - This is label for collapse button
 * @property {boolean} collapsible - This sets the behavior of component that if it is collapsible or not
 * @property {boolean} expanded - Hold the value if filter is in expanded state at given time
 * @property {string} id -  Sets the id of product variant
 */
export const propTypes = {
  variants: PropTypes.arrayOf(
    PropTypes.shape( {
      productId: PropTypes.string,
      skuId: PropTypes.string,
      name: PropTypes.string,
      shadeDescription: PropTypes.string,
      swatchImage: PropTypes.shape( {
        imageUrl: PropTypes.string,
        description: PropTypes.string
      } ),
      smooshImage: PropTypes.shape( {
        imageUrl: PropTypes.string,
        description: PropTypes.string
      } ),
      selected: PropTypes.bool,
      disabled: PropTypes.bool,
      unavailable: PropTypes.bool,
      listPrice: PropTypes.string,
      salePrice: PropTypes.string,
      selectAction: PropTypes.shape( {
        label: PropTypes.string,
        url: PropTypes.string,
        graphql: PropTypes.string
      } )
    } )
  ).isRequired,
  typeLabel: PropTypes.string,
  dimensionsLabel: PropTypes.string,
  dimensionsValue: PropTypes.string,
  shadeFinderAction: PropTypes.shape( {
    label: PropTypes.string,
    url: PropTypes.string
  } ),
  doneAction: PropTypes.object,
  closeAccessibilityAction: PropTypes.object,
  viewAllAction: PropTypes.shape( {
    label: PropTypes.string
  } ),
  viewLessLabel: PropTypes.string,
  collapsible: PropTypes.bool,
  expanded: PropTypes.bool,
  id: PropTypes.string
};

/**
 * Default values for passed properties
 * @type {object}
 */
export const defaultProps = {
  ...constants.INTERSECTION_OBSERVER_OPTIONS
};

ProductVariant.propTypes = propTypes;
ProductVariant.defaultProps = defaultProps;

export default ProductVariant;
